import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { AuthService } from "@/services/auth";
import { User } from "@/types"; // Your custom User type

interface AuthContextType {
	user: SupabaseUser | null;
	full_name?: string | null; // Optional full name
	profile: User | null; // Changed to your custom User type
	loading: boolean;
	signOut: () => Promise<void>;
	refreshProfile: () => Promise<void>;
	signIn: (email: string, password: string) => Promise<{ error: any }>;
	signUp: (data: {
		email: string;
		password: string;
		fullName: string;
		role: "parent" | "doula";
	}) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [user, setUser] = useState<SupabaseUser | null>(null);
	const [profile, setProfile] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const currentUserId = useRef<string | null>(null);

	const fetchProfile = async (userId: string) => {
		try {
			const { data, error } = await AuthService.getUserProfile(userId);

			if (error) {
				return null;
			}

			return data as User;
		} catch (error) {
			return null;
		}
	};

	const refreshProfile = async () => {
		if (user) {
			const profileData = await fetchProfile(user.id);
			setProfile(profileData);
		}
	};

	const signOut = async () => {
		await AuthService.signOut();
		setUser(null);
		setProfile(null);
	};

	const signIn = async (email: string, password: string) => {
		try {
			const { user: authUser, error } = await AuthService.signIn({
				email,
				password,
			});

			if (error) {
				return { error };
			}

			if (authUser) {
				setUser(authUser);
				const profileData = await fetchProfile(authUser.id);
				setProfile(profileData);
			}

			return { error: null };
		} catch (error) {
			return { error };
		}
	};

	const signUp = async (data: {
		email: string;
		password: string;
		fullName: string;
		role: "parent" | "doula";
	}) => {
		try {
			const { user: authUser, error } = await AuthService.signUp(data);

			if (error) {
				return { error };
			}

			if (authUser) {
				setUser(authUser);
				const profileData = await fetchProfile(authUser.id);
				setProfile(profileData);
			}

			return { error: null };
		} catch (error) {
			return { error };
		}
	};

	useEffect(() => {
		const getSession = async () => {
			try {
				const {
					data: { session },
				} = await supabase.auth.getSession();
				currentUserId.current = session?.user?.id ?? null;
				setUser(session?.user ?? null);

				if (session?.user) {
					// Add timeout to prevent hanging
					const profilePromise = fetchProfile(session.user.id);
					const timeoutPromise = new Promise((_, reject) =>
						setTimeout(() => reject(new Error("Profile fetch timeout")), 10000),
					);

					try {
						const profileData = await Promise.race([
							profilePromise,
							timeoutPromise,
						]);
						setProfile(profileData);
					} catch (error) {
						setProfile(null);
					}
				}
			} catch (error) {
				// Error handling silenced for production
			} finally {
				setLoading(false);
			}
		};

		getSession();

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			const nextUserId = session?.user?.id ?? null;

			// Token refreshes fire for the same user and only carry a new
			// session/user object reference - skip re-rendering consumers
			// (and re-fetching their data) when the user hasn't changed.
			if (event === "TOKEN_REFRESHED" && nextUserId === currentUserId.current) {
				return;
			}

			currentUserId.current = nextUserId;
			setUser(session?.user ?? null);

			if (session?.user) {
				// Add timeout to prevent hanging
				const profilePromise = fetchProfile(session.user.id);
				const timeoutPromise = new Promise((_, reject) =>
					setTimeout(() => reject(new Error("Profile fetch timeout")), 10000),
				);

				try {
					const profileData = await Promise.race([
						profilePromise,
						timeoutPromise,
					]);
					setProfile(profileData);
				} catch (error) {
					setProfile(null);
				}
			} else {
				setProfile(null);
			}
			setLoading(false);
		});

		return () => subscription.unsubscribe();
	}, []);

	const value = useMemo(
		() => ({
			user,
			profile,
			loading,
			signOut,
			refreshProfile,
			signIn,
			signUp,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[user, profile, loading],
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
};
